/**
 * Web wrapper: lichess's chessground, integrated to match the prop surface and
 * imperative API of the legacy `ChessboardWithArrow` so PlayChess can swap the
 * import without other changes.
 *
 * Architecture (Phase A of the chessground migration — see
 * chessground-migration-plan.md):
 *
 *   • One React component. Mounts chessground in a <div ref> and never re-renders
 *     during drag — chessground owns all drag/render state.
 *   • A `chess.js` instance (gameRef) provides legal moves, check detection,
 *     capture detection, and the from/to recovery for externally-applied FENs.
 *   • A "latest props" ref (propsRef) lets the drag handler always see the
 *     latest onPieceDropExtension / handleNewFen / restrictedPiece, so the
 *     handler bound into chessground at mount never goes stale.
 *
 * Premove: native chessground (`premovable.enabled` + `api.playPremove()`).
 * The 342-line v4 patch is replaced by the few `premovable` config fields.
 *
 * Promotion: rendered as a sibling <Promotion /> overlay, since chessground
 * has no built-in promotion UI.
 *
 * Native (iOS/Android) callers automatically get the legacy ChessboardWithArrow
 * via the `index.native.tsx` sibling — see the platform-extension convention.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Chess} from 'chess.js';
import {Chessground} from 'chessground';
import useSound from 'use-sound';
import 'chessground/assets/chessground.base.css';

// Move sounds, identical URLs to the legacy chessboard-package/ChessGame so the
// audio is unchanged. P2-3 in the perf plan calls for bundling these locally.
const SOUND_URLS = {
  move: 'https://images.chesscomfiles.com/chess-themes/sounds/_MP3_/default/move-self.mp3',
  capture:
    'https://images.chesscomfiles.com/chess-themes/sounds/_MP3_/default/capture.mp3',
  check:
    'https://images.chesscomfiles.com/chess-themes/sounds/_MP3_/default/move-check.mp3',
  castle:
    'https://images.chesscomfiles.com/chess-themes/sounds/_MP3_/default/castle.mp3',
};

// Infer the chessground Api type from the factory return so we don't have to
// import from `chessground/dist/api` (which some TS resolution modes can't
// find — see types.d.ts in this folder).
type CGApi = ReturnType<typeof Chessground>;
// chessground's Config object: pass-through to api.set / Chessground(), and
// our test surface stays small. Typed as `any` to avoid the same subpath
// import problem; chessground does the real type-checking at runtime.
type CGConfig = Parameters<typeof Chessground>[1] & Record<string, any>;

import Promotion from './Promotion';
import {injectChessgroundStyles} from './styles-injector';
import {
  applyMoveLoose,
  buildDests,
  didCaptureOccur,
  findMoveFromFens,
  flipTurnToMatch,
  isPremoveStillLegal,
  isPromotionMove,
  kingInCheckSquare,
  safeChess,
  themeClassName,
  turnColorOf,
  type Premove,
} from './helpers';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

type PieceCode = 'q' | 'r' | 'b' | 'n';

export type RestrictedPiece =
  | {
      type: string;
      square: string;
      color?: string;
    }
  | null
  | undefined;

export type ChessboardHandle = {
  clearPremoves: (clearLastPieceColour?: boolean) => void;
  attemptPremove: (color?: string) => void;
  premoves: Array<{from: string; to: string}>;
  resetArrows: () => void;
};

export type ChessboardProps = {
  position?: string;
  boardOrientation?: 'white' | 'black';
  disabled?: boolean;
  /**
   * Previous FEN. Only consulted at mount time to compute the initial
   * last-move highlight. After mount, chessground tracks `lastMove` itself.
   */
  lastMoveFen?: string | null;
  colorTheme?: string | null;
  arePremovesAllowed?: boolean;
  animationDuration?: number;
  /**
   * Show the a–h / 1–8 rank-file labels on the board edges. Defaults to true
   * (chessground's own default), so existing boards are unchanged. Pass false
   * for small preview/thumbnail boards where the labels are just clutter.
   */
  coordinates?: boolean;
  restrictedPiece?: RestrictedPiece;
  restrictPiece?: (piece: any, square: string) => void;
  /**
   * Truthy return blocks the move (chessground reverts to the prior FEN);
   * falsy/undefined return = commit. Matches the legacy `shallStop` contract.
   */
  onPieceDropExtension?: (from: string, to: string, promotion?: string) => any;
  /** Fires AFTER a local chess.move() succeeds (not on external positions). */
  handleNewFen?: (moveData: {
    from: string;
    to: string;
    piece: string;
    san: string;
    newFen: string;
    lastFen: string;
  }) => void;
};

type PendingPromotion = {
  from: string;
  to: string;
  color: 'w' | 'b';
  fenBeforeMove: string;
};

const Chessboard = forwardRef<ChessboardHandle, ChessboardProps>(
  function Chessboard(
    {
      position = 'start',
      boardOrientation = 'white',
      disabled = false,
      lastMoveFen,
      colorTheme,
      arePremovesAllowed = true,
      animationDuration = 10,
      coordinates = true,
      restrictedPiece,
      restrictPiece,
      onPieceDropExtension,
      handleNewFen,
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    /** Outer position:relative div — sized by the device-pixel snap effect. */
    const outerRef = useRef<HTMLDivElement | null>(null);
    const apiRef = useRef<CGApi | null>(null);
    /**
     * The "real" game — server-confirmed state. Drives the move that gets sent
     * to the server. Advanced by `commitLocalMove` (our move that the server
     * confirmed) and the position effect (opponent's move from socket).
     */
    const gameRef = useRef<Chess>(
      safeChess(position === 'start' ? STARTING_FEN : position, STARTING_FEN),
    );
    /**
     * The "speculative" game — `gameRef` with every queued premove applied on
     * top. Drives the `fen` chessground actually renders, so pieces visually
     * live at their premove destinations and chain premoves with the same
     * piece (Nf3 → Nd4 → Nb5) work. Kept in sync by `rebuildSpeculative()`.
     */
    const specGameRef = useRef<Chess>(new Chess(gameRef.current.fen()));
    /** The fen our `gameRef` is currently on; used to dedupe external prop changes. */
    const appliedFenRef = useRef<string>(gameRef.current.fen());
    /**
     * Queued premoves in order. Updated synchronously (mutation, not setState)
     * so chessground event handlers bound at mount always see the freshest
     * queue. The imperative `premoves` getter reads from here.
     */
    const premoveQueueRef = useRef<Premove[]>([]);

    // ---------- move sounds (declared above propsRef so we can stash them in it) ----------
    // Matches the legacy chessboard-package/ChessGame audio: 4 remote MP3s.
    // P2-3 in performance-fix-plan.md calls for bundling these locally.
    const [playMove] = useSound(SOUND_URLS.move);
    const [playCapture] = useSound(SOUND_URLS.capture);
    const [playCheck] = useSound(SOUND_URLS.check);
    const [playCastle] = useSound(SOUND_URLS.castle);

    // "Latest props" pattern: the chessground events callbacks are bound once
    // at mount, but read from this ref so they always see the freshest values.
    // THREE things in here change identity across renders and broke things:
    //   1. PlayChess's `makeMove` (onPieceDropExtension) is recreated each render.
    //   2. `useSound` returns a NEW play function reference once the audio loads
    //      — the function returned on the FIRST render is a no-op. Without the
    //      ref, sounds on user moves silently dropped (only opponent-move sound
    //      worked, because the position useEffect captures a fresh closure each
    //      run). This was the "sounds only on opponent moves" bug.
    //   3. `boardOrientation` determines `movable.color`, which gates premove
    //      queueing in chessground.
    const propsRef = useRef({
      onPieceDropExtension,
      handleNewFen,
      restrictedPiece,
      restrictPiece,
      disabled,
      boardOrientation,
      playMove,
      playCapture,
      playCheck,
      playCastle,
    });
    useEffect(() => {
      propsRef.current = {
        onPieceDropExtension,
        handleNewFen,
        restrictedPiece,
        restrictPiece,
        disabled,
        boardOrientation,
        playMove,
        playCapture,
        playCheck,
        playCastle,
      };
    });

    /**
     * Pick the right sound for a just-completed move. Capture > castle > check >
     * normal — matches legacy precedence. Reads play funcs from propsRef so it
     * always uses the post-load Howl instances, not the load-time no-ops.
     */
    const playMoveSound = useCallback(
      (captured: boolean, castled: boolean, inCheck: boolean) => {
        const {
          playMove: pM,
          playCapture: pCap,
          playCheck: pChk,
          playCastle: pCas,
        } = propsRef.current;
        try {
          if (captured) {
            pCap();
          } else if (castled) {
            pCas();
          } else if (inCheck) {
            pChk();
          } else {
            pM();
          }
        } catch {
          /* audio context may not be ready; ignore */
        }
      },
      [],
    );

    const [lastMove, setLastMove] = useState<[string, string] | undefined>(
      () => {
        if (!lastMoveFen) {
          return undefined;
        }
        const m = findMoveFromFens(
          lastMoveFen,
          position === 'start' ? STARTING_FEN : position,
        );
        return m || undefined;
      },
    );
    const lastMoveRef = useRef(lastMove);
    useEffect(() => {
      lastMoveRef.current = lastMove;
    });

    const [pendingPromotion, setPendingPromotion] =
      useState<PendingPromotion | null>(null);

    // Inject pieces + theme CSS once on first mount of any Chessboard.
    useEffect(injectChessgroundStyles, []);

    /**
     * Combine the last-move highlight (from realGame), the premove highlights
     * (every from/to of every queued premove), and the restricted-piece
     * highlight (touch-and-move rule) into a single `Map<square, className>`
     * for chessground's `highlight.custom`.
     *
     * Priority (later wins, since `Map.set` overwrites):
     *   last-move  <  premove  <  restricted-piece
     *
     * Restricted-piece wins because it tells the user the move is MANDATORY
     * — that information should always be visible even if the same square is
     * also a last-move target or a premove participant.
     */
    const buildHighlightMap = useCallback(():
      | Map<string, string>
      | undefined => {
      const lm = lastMoveRef.current;
      const queue = premoveQueueRef.current;
      const rp = propsRef.current.restrictedPiece;
      const rpSquare = rp && rp.square ? rp.square : null;
      if (!lm && queue.length === 0 && !rpSquare) {
        return undefined;
      }
      const map = new Map<string, string>();
      if (lm) {
        map.set(lm[0], 'cc-last-move-from');
        map.set(lm[1], 'cc-last-move-to');
      }
      for (const pm of queue) {
        map.set(pm.from, 'cc-premove-from');
        map.set(pm.to, 'cc-premove-to');
      }
      if (rpSquare) {
        map.set(rpSquare, 'cc-restricted-piece');
      }
      return map;
    }, []);

    /**
     * Rebuild `specGameRef` from `gameRef` + every queued premove. If any
     * queued premove is illegal on the (possibly updated) real position,
     * cut the queue at that point — chain breaks. Called after gameRef
     * advances (commitLocalMove, position effect) or after the queue
     * changes (enqueuePremove, clearPremoves).
     */
    const rebuildSpeculative = useCallback(() => {
      let spec = new Chess(gameRef.current.fen());
      const queue = premoveQueueRef.current;
      let cutAt = queue.length;
      for (let i = 0; i < queue.length; i++) {
        const pm = queue[i];
        const piece = spec.get(pm.from as any);
        // Same-piece-still-there check; if the piece is gone or has changed
        // color/type, the chain is broken from here on.
        if (!piece || piece.color + piece.type !== pm.pieceCodeAtQueue) {
          cutAt = i;
          break;
        }
        // chess.js refuses out-of-turn moves; flip the turn marker first
        // so the strict-move path inside applyMoveLoose has a fair chance.
        if (spec.turn() !== piece.color) {
          spec = flipTurnToMatch(spec, piece.color as 'w' | 'b');
        }
        // applyMoveLoose handles both legal moves (via chess.move) and
        // "anticipatory" geometric premoves (pawn diagonal to empty, capture
        // own piece, etc.) via direct FEN manipulation. Returns null only if
        // even the manual piece-shuffle yields a position chess.js refuses
        // to load (e.g. king captured) — in which case the chain breaks
        // here.
        const newSpec = applyMoveLoose(spec, pm.from, pm.to, pm.promotion);
        if (!newSpec) {
          cutAt = i;
          break;
        }
        spec = newSpec;
      }
      if (cutAt < queue.length) {
        premoveQueueRef.current = queue.slice(0, cutAt);
      }
      specGameRef.current = spec;
    }, []);

    /**
     * Push the speculative position (real + queue) to chessground. Sets fen,
     * turnColor (CRUCIAL — chessground's userMove flips turnColor before
     * events.after, and api.set({fen}) alone does NOT re-derive it), lastMove
     * + premove highlights, check, and movable. This is the SINGLE place that
     * decides what chessground renders for any non-mount update.
     */
    const renderSpeculative = useCallback(() => {
      const api = apiRef.current;
      if (!api) {
        return;
      }
      const spec = specGameRef.current;
      const {
        disabled: isDisabled,
        restrictedPiece: rp,
        boardOrientation: orientation,
      } = propsRef.current;
      api.set({
        fen: spec.fen(),
        turnColor: turnColorOf(spec),
        check: spec.inCheck() ? turnColorOf(spec) : false,
        highlight: {
          lastMove: false,
          check: true,
          custom: buildHighlightMap() as any,
        },
        movable: {
          color: orientation,
          dests: isDisabled ? new Map() : buildDests(spec, rp?.square),
        },
      });
    }, [buildHighlightMap]);

    /**
     * User attempted a move when it's NOT their real turn (opponent's turn) or
     * when there's already a chain queued. Push to the queue, advance the
     * speculative game, re-render. Auto-queens promotions (Phase 1 — inline
     * picker for premove promotions is Phase 2 polish).
     */
    /**
     * Clear all queued premoves and re-render. Idempotent. Called from:
     *   - the imperative `clearPremoves()` ref method
     *   - right-click cancellation on the board (any right-click clears all)
     *   - left-click on an empty square with nothing selected (cancel gesture)
     *   - the position effect, when an opponent's move makes the first queued
     *     premove illegal (chain broken)
     */
    const clearPremoveQueue = useCallback(() => {
      if (premoveQueueRef.current.length === 0) {
        return;
      }
      premoveQueueRef.current = [];
      rebuildSpeculative();
      renderSpeculative();
    }, [rebuildSpeculative, renderSpeculative]);

    const enqueuePremove = useCallback(
      (from: string, to: string) => {
        let spec = specGameRef.current;
        const piece = spec.get(from as any);
        if (!piece) {
          renderSpeculative();
          return;
        }
        // chess.js refuses out-of-turn moves; flip turn first so the strict
        // path inside applyMoveLoose has a fair chance for legal premoves.
        if (spec.turn() !== piece.color) {
          spec = flipTurnToMatch(spec, piece.color as 'w' | 'b');
        }
        const isPromo = isPromotionMove(spec, from, to);
        const promotion: PieceCode | undefined = isPromo ? 'q' : undefined;
        // applyMoveLoose: try chess.move first (handles castling, en passant,
        // legal promotion correctly), fall back to manual FEN piece manipulation
        // for moves that chessground's premove geometry accepts but chess.js
        // refuses — pawn diagonal to empty square (anticipating opponent
        // arriving), capturing own piece (only valid in a chain after an
        // earlier premove moves the own piece away), bishop/rook/queen through
        // an own piece, etc. Returns null only if the resulting FEN is too
        // broken for chess.js to load at all.
        const newSpec = applyMoveLoose(spec, from, to, promotion);
        if (!newSpec) {
          renderSpeculative();
          return;
        }
        specGameRef.current = newSpec;
        premoveQueueRef.current = [
          ...premoveQueueRef.current,
          {
            from,
            to,
            promotion,
            pieceCodeAtQueue: piece.color + piece.type,
          },
        ];
        renderSpeculative();
      },
      [renderSpeculative],
    );

    // ---------- commit a local move (user drop or promotion completion) ----------
    /**
     * Commit a move to the REAL game (gameRef). Returns true on success, false
     * if blocked by `onPieceDropExtension` or refused by chess.js. The caller
     * is responsible for any chain-cancellation policy when false is returned
     * (the position effect clears the remaining queue on a failed replay).
     *
     * On success, advances gameRef, plays sound, fires handleNewFen, then
     * rebuilds + re-renders the speculative board (which absorbs the move
     * into the real position and reapplies any remaining queued premoves on
     * top).
     */
    const commitLocalMove = useCallback(
      (from: string, to: string, promotion?: PieceCode): boolean => {
        const game = gameRef.current;
        const api = apiRef.current;
        if (!api) {
          return false;
        }
        const {onPieceDropExtension: extFn, handleNewFen: newFenFn} =
          propsRef.current;

        const lastFen = game.fen();

        // Extension intercept: truthy return blocks the move.
        let block = false;
        try {
          block = !!extFn?.(from, to, promotion);
        } catch {
          block = true;
        }
        if (block) {
          renderSpeculative();
          return false;
        }

        // Apply on our chess.js. chess.js v1 throws on illegal moves.
        let move: any;
        try {
          move = game.move({from, to, promotion: promotion || 'q'});
        } catch {
          move = null;
        }
        if (!move) {
          renderSpeculative();
          return false;
        }

        const newFen = game.fen();
        appliedFenRef.current = newFen;
        const newLastMove: [string, string] = [move.from, move.to];
        // Update both state (next render) and ref (sync — so renderSpeculative
        // below picks up the new last-move highlight immediately).
        setLastMove(newLastMove);
        lastMoveRef.current = newLastMove;

        // Real game advanced. Rebuild speculative from the new real + the
        // remaining queue, then render.
        rebuildSpeculative();
        renderSpeculative();

        // Sound for the local move — chess.js gives us all the inputs.
        const flags: string = move.flags || '';
        playMoveSound(
          !!move.captured,
          flags.includes('k') || flags.includes('q'),
          game.inCheck(),
        );

        newFenFn?.({
          from: move.from,
          to: move.to,
          piece: move.piece,
          san: move.san,
          newFen,
          lastFen,
        });

        return true;
      },
      [playMoveSound, rebuildSpeculative, renderSpeculative],
    );

    // ---------- chessground -> us: user touched a square (click or drag-start) ----------
    // Two responsibilities:
    //   1. Touch-and-move rule: fire restrictPiece for piece touches.
    //   2. Left-click cancel for "truly empty" squares — but only when the
    //      user isn't in the middle of a click-to-move sequence.
    //
    // The cancel rule has FOUR cases:
    //   - Click on a square with a piece in spec → no cancel (it's a piece
    //     touch — touch-and-move logic runs).
    //   - Click on a premove FROM-square (empty in spec, but the piece is
    //     still there in real because the premove only moved it in spec) →
    //     no cancel. User might be reconsidering that premove and the
    //     square is "meaningful."
    //   - Click on a truly empty square WHILE another square is selected
    //     (state.selected !== undefined) → no cancel. This is the second
    //     click of a click-to-move sequence; chessground's selectSquare will
    //     synchronously fire userMove → events.set → enqueuePremove right
    //     after our handler returns.
    //   - Click on a truly empty square with nothing selected → CANCEL.
    //     This is an explicit "user is reaching for a non-square" gesture.
    //
    // We deliberately do NOT use a deferred microtask — earlier versions
    // did, but the cancel-check ran before events.set fired in some click
    // patterns (false positives that wiped chained premoves). The
    // state.selected check above gives us the same disambiguation
    // synchronously, without races.
    const onSquareSelect = useCallback(
      (key: string) => {
        const spec = specGameRef.current;
        const piece = spec.get(key as any);

        // Case 1: piece in spec. Touch-and-move callback — never a cancel.
        if (piece) {
          const restrictFn = propsRef.current.restrictPiece;
          if (restrictFn) {
            try {
              restrictFn(
                {type: piece.type, square: key, color: piece.color} as any,
                key,
              );
            } catch {
              /* PlayChess.restrictPiece may throw on edge cases; isolate. */
            }
          }
          return;
        }

        // Nothing to cancel.
        if (premoveQueueRef.current.length === 0) {
          return;
        }

        // Case 2: premove FROM-square. The piece moved away in spec but is
        // still on this square in the real game. Treat as meaningful — no cancel.
        const real = gameRef.current;
        if (real.get(key as any)) {
          return;
        }

        // Empty square in both spec and real. This MIGHT be a click-to-move
        // destination (in which case events.set will fire shortly and enqueue
        // a premove) OR a fresh cancel gesture (no follow-up). Discriminate by
        // deferring the cancel and checking whether the queue grew.
        //
        // CRITICAL: we use setTimeout(0), NOT queueMicrotask. The app uses
        // @sentry/react, whose browser-api instrumentation wraps chessground's
        // callUserFunction in setTimeout (visible in the call stack — events
        // are fired one macrotask later than chessground intends). With a
        // microtask we'd run BEFORE Sentry's setTimeout for events.set, see
        // no queue change, and wrongly cancel. setTimeout(0) is enqueued in
        // the same macrotask queue as Sentry's deferred events.set, so it
        // runs AFTER — by which time enqueuePremove has incremented the
        // queue and our snapshot check correctly skips the cancel.
        //
        // Also note: we cannot use state.selected to detect click-to-move
        // intent. By the time our handler runs (Sentry-deferred), chessground
        // has already executed userMove (which calls unselect → clears
        // state.selected) inside its synchronous selectSquare body, so the
        // read always sees undefined for empty-square clicks.
        const queueLenAtClick = premoveQueueRef.current.length;
        const specFenAtClick = specGameRef.current.fen();
        setTimeout(() => {
          if (
            premoveQueueRef.current.length === queueLenAtClick &&
            specGameRef.current.fen() === specFenAtClick
          ) {
            clearPremoveQueue();
          }
        }, 0);
      },
      [clearPremoveQueue],
    );

    // Right-click anywhere on the board cancels all queued premoves.
    //
    // This handler is wired via `onMouseDownCapture` (capture phase) so it
    // runs BEFORE chessground's own mousedown listener on the inner cg-board
    // (which attaches in the bubble phase to start arrow drawing). Bubble
    // order from the inner element back up to our outer div was unreliable:
    // chessground's drawable handler can effectively prevent the event from
    // reaching us in some browsers / under some interaction sequences, so
    // right-click clear was silently dropped. Capture phase always fires
    // first regardless. We do NOT call preventDefault — chessground still
    // gets its bubble-phase event for arrow drawing, so right-click-drag
    // for arrows continues to work in parallel.
    const onContainerMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (e.button !== 2) {
          return;
        }
        clearPremoveQueue();
      },
      [clearPremoveQueue],
    );

    // ---------- chessground -> us: user dropped a piece ----------
    // Dispatch:
    //   - Real turn AND queue empty → real move. If a promotion, show the
    //     picker (only path that shows the modal — premove promotions auto-queen).
    //   - Otherwise (opponent's turn OR chained premove on the speculative
    //     board) → enqueue. enqueuePremove auto-queens promotions.
    const onUserMove = useCallback(
      (orig: string, dest: string) => {
        const game = gameRef.current;
        const realTurn = turnColorOf(game);
        const userColor = propsRef.current.boardOrientation;
        const queueEmpty = premoveQueueRef.current.length === 0;

        if (realTurn === userColor && queueEmpty) {
          // Real move.
          if (isPromotionMove(game, orig, dest)) {
            const piece = game.get(orig as any);
            if (!piece) {
              return;
            }
            setPendingPromotion({
              from: orig,
              to: dest,
              color: piece.color as 'w' | 'b',
              fenBeforeMove: game.fen(),
            });
            return;
          }
          commitLocalMove(orig, dest);
          return;
        }

        // Premove.
        enqueuePremove(orig, dest);
      },
      [commitLocalMove, enqueuePremove],
    );

    // ---------- chessground mount/destroy (once) ----------
    useEffect(() => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const game = gameRef.current;

      const initialConfig: CGConfig = {
        fen: game.fen(),
        orientation: boardOrientation,
        coordinates,
        turnColor: turnColorOf(game),
        lastMove: lastMove ? (lastMove as any) : undefined,
        check: kingInCheckSquare(game) ? turnColorOf(game) : false,
        // IMPORTANT: do NOT mount with viewOnly: true. chessground's bindBoard
        // (events.js) checks `if (s.viewOnly) return;` once at mount and skips
        // attaching the mousedown/touchstart listeners entirely — flipping
        // viewOnly later via api.set does NOT re-bind them, so the board stays
        // dead. We achieve the "disabled" effect via draggable.enabled,
        // selectable.enabled, premovable.enabled, and an empty dests map.
        viewOnly: false,
        animation: {enabled: true, duration: animationDuration},
        movable: {
          free: false,
          color: propsRef.current.boardOrientation,
          showDests: true,
          dests: disabled
            ? new Map()
            : buildDests(game, restrictedPiece?.square),
          events: {after: onUserMove as any},
        },
        // Native chessground premove is enabled SOLELY so chessground's
        // canPremove path fires `premovable.events.set` when the user makes
        // a wrong-turn move attempt (the very first premove of a chain,
        // before our renderSpeculative has flipped chessground's turnColor).
        // We immediately translate it to our own queue and cancel chessground's
        // single-slot premove state in the callback. Chained premoves (when
        // chessground.turnColor already matches the user after a prior premove
        // flipped it) fire `movable.events.after` instead — see onUserMove.
        // `showDests: true` so during selection the user sees the small blue
        // dots chessground renders on theoretically-valid premove destinations
        // (see square.premove-dest CSS). These are separate from the chunky
        // cc-premove-from/to highlights, which only appear after the premove
        // is queued.
        premovable: {
          enabled: arePremovesAllowed && !disabled,
          showDests: true,
          castle: true,
          events: {
            set: (orig: string, dest: string) => {
              enqueuePremove(orig, dest);
              // Clear chessground's internal single-slot premove so its own
              // `.current-premove` highlight doesn't conflict with ours, and
              // so subsequent premove attempts start clean.
              apiRef.current?.cancelPremove();
              // chessground's selectSquare unconditionally sets
              // state.selected = clicked_key at the END of its handler —
              // AFTER our events.set fires. For the CLICK flow this leaves
              // state.selected pointing at the premove's destination, and
              // the next click on a piece is interpreted as
              // userMove(prev_destination, clicked_piece). If the geometry
              // matches a premove pattern (often does — bishop diagonals,
              // knight L-shapes, pawn forward) chessground queues a stray
              // nonsense premove (e.g. knight "captures" own piece).
              // Defer the clear via microtask so it runs AFTER selectSquare
              // completes; otherwise our selectSquare(null) would be
              // overwritten by the trailing `state.selected = key`. Drag
              // doesn't hit this because dragend already unselects without
              // re-setting selected.
              queueMicrotask(() => {
                apiRef.current?.selectSquare(null);
              });
            },
          },
        },
        draggable: {enabled: !disabled, showGhost: true},
        selectable: {enabled: !disabled},
        drawable: {
          enabled: true,
          visible: true,
          defaultSnapToValidMove: true,
          eraseOnClick: true,
        },
        // lastMove: false suppresses chessground's single-class default
        // (which colors both from-square and to-square the same). Our two-
        // shade highlight (and the premove highlights) come from
        // `highlight.custom` via buildHighlightMap.
        highlight: {
          lastMove: false,
          check: true,
          custom: buildHighlightMap() as any,
        },
        events: {
          // Fires on any user selection (click + drag-start). Used for the
          // touch-and-move rule: PlayChess.restrictPiece consumes this.
          select: onSquareSelect as any,
        },
      };
      const api = Chessground(el, initialConfig);
      apiRef.current = api;
      return () => {
        api.destroy();
        apiRef.current = null;
      };
      // Mount-only; subsequent prop changes flow through the dedicated effects.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------- keep hit-test bounds fresh when the board is MOVED ----------
    // chessground hit-tests pointers with the absolute `bounds.left/top`
    // (board.js getKeyAtDomPos) but positions pieces with only `bounds.width/
    // height` (util.js posToTranslate). The bounds live in a lazy memo
    // (state.dom.bounds) that chessground invalidates only on window `resize`,
    // document `scroll` (capture), and a ResizeObserver on the board's OWN
    // wrap — i.e. when the board changes SIZE, never when it merely MOVES.
    // When the PlayChess player rows above the board stream in asynchronously
    // (opponent name/rating over the socket), the board is pushed down WITHOUT
    // a size change, the cached `top` goes stale, and taps resolve one rank
    // off the piece while the pieces still LOOK correct ("press the pawn, the
    // queen below selects"). Device/load-speed dependent, and unreproducible
    // by resizing the viewport, because a resize is the very thing that clears
    // the cache.
    //
    // Invalidation here is `state.dom.bounds.clear()` — O(1), non-destructive,
    // and safe at ANY moment: drag (drag.js processDrag/end) and arrow drawing
    // (draw.js processDraw) re-read the memo every animation frame, so a
    // mid-gesture clear is simply picked up on the next frame. (An earlier
    // version called api.redrawAll() instead — that rebuilds the board DOM,
    // kills in-flight gestures, and leaks one chessground ResizeObserver on
    // the persistent wrap per call, which forced fragile move-detection
    // thresholds and pointer-down deferral. Sub-threshold drifts could then
    // accumulate into a few-px stale offset — the residual "slight stagger"
    // on some machines. The memo clear needs none of that machinery.)
    //
    // Two layers:
    //  1. Gesture-time guarantee: clear on capture-phase mousedown/touchstart
    //     on the wrap. Capture on the ancestor runs BEFORE chessground's
    //     bubble-phase handlers on the inner cg-board, so every gesture
    //     hit-tests a freshly computed rect regardless of what moved the board
    //     or whether any observer noticed. Cost: one getBoundingClientRect per
    //     gesture start.
    //  2. Reflow watcher: also clear when any ancestor (up to <body>) resizes
    //     — the signal chessground's wrap-only observer cannot see — and on
    //     focus / visibilitychange / orientationchange (tab restore, mobile
    //     URL-bar collapse, rotation). Keeps non-gesture bounds consumers
    //     (drag frames already in flight, arrow snapping) fresh between
    //     gestures too.
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;

      const clearCgBounds = () => {
        // Api exposes its raw state (api.js:20); dom.bounds is util.memo with
        // .clear(). Optional-chain every hop: `dom` is absent until the first
        // redrawAll finishes, and the inferred CGApi type hides the internals
        // (hence the cast).
        (apiRef.current as any)?.state?.dom?.bounds?.clear?.();
      };

      el.addEventListener('mousedown', clearCgBounds, {
        capture: true,
        passive: true,
      });
      el.addEventListener('touchstart', clearCgBounds, {
        capture: true,
        passive: true,
      });
      window.addEventListener('focus', clearCgBounds);
      window.addEventListener('orientationchange', clearCgBounds);
      document.addEventListener('visibilitychange', clearCgBounds);

      let ro: ResizeObserver | undefined;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(clearCgBounds);
        let node: HTMLElement | null = el.parentElement;
        let hops = 0;
        while (node && hops < 8) {
          ro.observe(node);
          if (node === document.body) break;
          node = node.parentElement;
          hops += 1;
        }
        if (document.body) ro.observe(document.body);
      }

      return () => {
        ro?.disconnect();
        el.removeEventListener('mousedown', clearCgBounds, {capture: true});
        el.removeEventListener('touchstart', clearCgBounds, {capture: true});
        window.removeEventListener('focus', clearCgBounds);
        window.removeEventListener('orientationchange', clearCgBounds);
        document.removeEventListener('visibilitychange', clearCgBounds);
      };
      // Mount-only; reads the live board via the stable containerRef/apiRef.
    }, []);

    // ---------- device-pixel-exact board sizing ----------
    // chessground's updateBounds (render.js) snaps its inner cg-container to
    // floor(wrapWidth * dpr / 8) * 8 / dpr so every square is a whole number
    // of DEVICE pixels — but our wrap was width:100%, so the snapped container
    // sat top-left inside a wrap up to 8 device px larger. That slack is
    // screen-dependent (exact container width × devicePixelRatio — Windows
    // 125/150% scaling, browser zoom): a dead strip at the right/bottom edges
    // where taps miss the board, and the Promotion overlay (percent-positioned
    // against the outer div == wrap box) drifting up to ~8px off the actual
    // squares — a visible "appearance vs selection" stagger on some screens.
    // Pre-snapping the outer box with chessground's own formula makes
    // wrap == container == board exactly.
    useEffect(() => {
      const outer = outerRef.current;
      if (!outer || typeof ResizeObserver === 'undefined') return;

      let raf = 0;
      let lastDpr = window.devicePixelRatio || 1;
      let mmCleanup: (() => void) | null = null;

      // matchMedia is the only signal for devicePixelRatio changes that do
      // not reflow the layout (window moved to a differently-scaled monitor,
      // OS scale change). The query is dpr-specific, so re-chain a fresh
      // listener after every change (standard pattern).
      const watchDpr = () => {
        mmCleanup?.();
        const mq = window.matchMedia(
          `(resolution: ${window.devicePixelRatio || 1}dppx)`,
        );
        const onChange = () => snap();
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if ((mq as any).addListener) (mq as any).addListener(onChange);
        mmCleanup = () => {
          if (mq.removeEventListener) {
            mq.removeEventListener('change', onChange);
          } else if ((mq as any).removeListener) {
            (mq as any).removeListener(onChange);
          }
        };
      };

      // Track the applied size as a NUMBER — comparing against style.width
      // strings is unreliable because CSSOM may re-serialize fractional px
      // (e.g. "392.72727272727275px" reads back as "392.727px"), which would
      // defeat both the no-op check and the dpr-change branch below.
      let lastSize = 0;

      const snap = () => {
        const parent = outer.parentElement;
        if (!parent) return;
        const dpr = window.devicePixelRatio || 1;
        // Content width of the parent (client box minus padding).
        const cs = getComputedStyle(parent);
        const avail =
          parent.clientWidth -
          parseFloat(cs.paddingLeft || '0') -
          parseFloat(cs.paddingRight || '0');
        if (!avail || avail <= 0) return; // hidden / not laid out yet
        // No minimum clamp: FunChallenges renders legitimate ~96px preview
        // boards, and an arbitrary floor would overflow small containers.
        const size = (Math.floor((avail * dpr) / 8) * 8) / dpr;
        if (size <= 0) return; // container narrower than 8 device px
        const dprChanged = dpr !== lastDpr;
        lastDpr = dpr;
        if (Math.abs(size - lastSize) > 0.01) {
          lastSize = size;
          outer.style.width = `${size}px`;
          outer.style.height = `${size}px`;
          // The wrap resizes with us (height/width 100%), so chessground's own
          // wrap observer fires and re-runs updateBounds for the new size.
        } else if (dprChanged) {
          // Same CSS size under a NEW dpr (monitor switch): chessground's wrap
          // observer won't fire, so its container snap is stale for the new
          // dpr. Force a full recompute — rare and deliberate (this is the one
          // remaining redrawAll call; it costs one leaked chessground observer
          // per monitor switch, an upstream quirk we accept here). redrawAll
          // rebuilds the board DOM, so NEVER run it while a drag or arrow-draw
          // is in flight — it would orphan the dragged piece element and let a
          // move commit invisibly. Skipping is safe: hit-tests stay consistent
          // with the live rect either way; the next genuine resize reconciles
          // the sub-pixel snap.
          const st: any = (apiRef.current as any)?.state;
          if (st && !st.draggable?.current && !st.drawable?.current) {
            apiRef.current?.redrawAll();
          }
        }
        // The media query only matches the dpr it was built for — re-chain
        // when dpr changes (and on first run).
        if (dprChanged || !mmCleanup) watchDpr();
      };

      // Defer RO-triggered snaps to the next frame: writing outer.style inside
      // the observer callback can otherwise trigger "ResizeObserver loop"
      // warnings when the parent's height tracks ours.
      const schedule = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(snap);
      };
      const ro = new ResizeObserver(schedule);
      if (outer.parentElement) ro.observe(outer.parentElement);
      snap();

      return () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        mmCleanup?.();
      };
      // Mount-only; reads live layout via outerRef.
    }, []);

    // ---------- prop sync: external position change ----------
    // Drives both opponent-move handling and premove replay:
    //   1. Load incoming FEN into the REAL game.
    //   2. Play sound for the opponent's move.
    //   3. If queue is non-empty, try to play the first premove against the
    //      new real position via commitLocalMove (which sends to the socket).
    //      - On success: remaining queue stays; commitLocalMove already
    //        rebuilt+rendered the speculative board.
    //      - On illegal: cancel the entire chain (matches lichess behavior).
    //      - On commit-blocked / refused: also cancel the chain (defensive).
    //   4. If queue is empty, just rebuild+render the speculative position
    //      (== the new real position) so chessground reflects the opponent's
    //      move.
    useEffect(() => {
      const api = apiRef.current;
      if (!api) {
        return;
      }
      const game = gameRef.current;
      const incoming = position === 'start' ? STARTING_FEN : position;
      if (incoming === appliedFenRef.current) {
        return;
      } // already applied

      // Prefer the `lastMoveFen` PROP (= PlayChess's `previousFen`, the FEN
      // before the navigated position) as the diff base, falling back to our
      // gameRef's current FEN. This matters when the user navigates a past
      // move via the notation panel: gameRef may be at, say, move 10, but the
      // user just clicked move 5 — `gameRef.fen()` and the incoming FEN are
      // not one move apart, so findMoveFromFens would return null and the
      // last-move highlight would stay stale. lastMoveFen is always one move
      // back from incoming (PlayChess sets both atomically), so the diff
      // always succeeds. For normal opponent moves the two are equal, so
      // nothing changes.
      const previousFen =
        lastMoveFen && lastMoveFen.length > 0 ? lastMoveFen : game.fen();
      const move = findMoveFromFens(previousFen, incoming);

      try {
        game.load(incoming);
      } catch {
        return; // bad FEN; ignore
      }
      appliedFenRef.current = incoming;
      if (move) {
        setLastMove(move);
        lastMoveRef.current = move; // sync for buildHighlightMap below
      } else {
        // Couldn't recover a single-move diff (e.g. a non-adjacent notation
        // jump with no lastMoveFen provided). Clear the highlight rather
        // than leave a stale one for a different position.
        setLastMove(undefined);
        lastMoveRef.current = undefined;
      }

      // Sound for the externally-applied move (opponent's move from socket
      // or a notation-panel navigation). We can't tell castle from FEN diff
      // alone (would need to replay) — for these paths we fall back to
      // capture/check/normal precedence. Same compromise as the legacy
      // package.
      if (move) {
        playMoveSound(
          didCaptureOccur(previousFen, incoming),
          false,
          game.inCheck(),
        );
      }

      // Premove replay.
      const queue = premoveQueueRef.current;
      const arePremovesEnabled =
        propsRef.current.disabled === false &&
        // arePremovesAllowed is captured in this closure each time the effect
        // runs; that's fine because the effect re-runs on position changes.
        arePremovesAllowed;

      if (queue.length > 0 && arePremovesEnabled) {
        const first = queue[0];
        if (isPremoveStillLegal(game, first)) {
          // Consume one. Route through commitLocalMove so PlayChess's
          // onPieceDropExtension fires (sends to socket) and gameRef advances.
          // commitLocalMove will rebuild+render the speculative on success.
          premoveQueueRef.current = queue.slice(1);
          const success = commitLocalMove(
            first.from,
            first.to,
            first.promotion,
          );
          if (!success) {
            // PlayChess rejected our premove. Break the chain.
            premoveQueueRef.current = [];
            rebuildSpeculative();
            renderSpeculative();
          }
        } else {
          // First premove no longer legal on the new real position. Cancel
          // the whole chain (matches lichess "broken chain" UX).
          premoveQueueRef.current = [];
          rebuildSpeculative();
          renderSpeculative();
        }
      } else {
        // No queue (or premoves disabled). Just render the new real position.
        rebuildSpeculative();
        renderSpeculative();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position]);

    // ---------- prop sync: orientation ----------
    // Flipping the board changes the user's color (in PlayChess at least —
    // boardOrientation === user's color). renderSpeculative reads
    // boardOrientation from propsRef so it reflects the latest, and applies
    // the new movable.color + dests in one call along with chessground's
    // `orientation`.
    useEffect(() => {
      const api = apiRef.current;
      if (!api) {
        return;
      }
      api.set({orientation: boardOrientation});
      renderSpeculative();
    }, [boardOrientation, renderSpeculative]);

    // ---------- prop sync: disabled / restricted / arePremovesAllowed ----------
    // viewOnly is intentionally NOT touched here (it stays `false` from the
    // mount config — see the comment there about chessground's bindBoard
    // skipping listener attachment if viewOnly was true at mount).
    // renderSpeculative builds movable.dests appropriately based on disabled
    // and restrictedPiece, so we just need to flip draggable/selectable and
    // premovable.enabled here. Passing only `enabled` inside premovable
    // (not `events`) leaves the events.set callback we bound at mount intact
    // — chessground's configure() deep-merges so other premovable fields
    // are preserved.
    useEffect(() => {
      const api = apiRef.current;
      if (!api) {
        return;
      }
      api.set({
        draggable: {enabled: !disabled, showGhost: true},
        selectable: {enabled: !disabled},
        premovable: {enabled: arePremovesAllowed && !disabled},
      });
      renderSpeculative();
    }, [
      disabled,
      arePremovesAllowed,
      restrictedPiece?.square,
      renderSpeculative,
    ]);

    // ---------- prop sync: animation duration ----------
    useEffect(() => {
      apiRef.current?.set({
        animation: {enabled: true, duration: animationDuration},
      });
    }, [animationDuration]);

    // ---------- promotion completion / cancel ----------
    // The promotion modal is only shown for REAL moves (when queue is empty
    // and it's the user's real turn — see onUserMove). Premove promotions
    // auto-queen via enqueuePremove. So at promotion time, spec == real and
    // renderSpeculative correctly restores the pre-promotion board.
    const onPromotionSelect = useCallback(
      (piece: PieceCode) => {
        const pending = pendingPromotion;
        if (!pending) {
          return;
        }
        // chessground visually moved the pawn to the last rank; restore the
        // pre-promotion position first (renderSpeculative reads gameRef which
        // is still pre-promotion since we deferred the commit), then run the
        // standard commit pipeline with the chosen piece.
        renderSpeculative();
        setPendingPromotion(null);
        commitLocalMove(pending.from, pending.to, piece);
      },
      [pendingPromotion, commitLocalMove, renderSpeculative],
    );
    const onPromotionCancel = useCallback(() => {
      const pending = pendingPromotion;
      if (!pending) {
        return;
      }
      // Full revert (fen + turnColor + lastMove + check + dests). Just setting
      // fen on chessground would leave turnColor flipped to the opponent and
      // every subsequent move would silently queue as a premove. gameRef
      // hasn't been advanced yet (we deferred the commit when showing the
      // modal), so renderSpeculative restores the pre-promotion board.
      renderSpeculative();
      setPendingPromotion(null);
    }, [pendingPromotion, renderSpeculative]);

    // ---------- imperative API for callers (PlayChess, Puzzles) ----------
    useImperativeHandle(
      ref,
      () => ({
        clearPremoves: (_clearLastPieceColour?: boolean) => {
          clearPremoveQueue();
        },
        attemptPremove: (_color?: string) => {
          // No-op. The position effect automatically replays the first
          // queued premove whenever a new opponent position arrives. Kept
          // for API compatibility with the legacy package.
        },
        get premoves() {
          // Expose a SHALLOW COPY so callers can iterate without seeing
          // mutations from the queue. Shape matches the legacy contract:
          // an array of {from, to}.
          return premoveQueueRef.current.map(p => ({from: p.from, to: p.to}));
        },
        resetArrows: () => {
          apiRef.current?.setShapes([]);
        },
      }),
      [clearPremoveQueue],
    );

    const themeClass = useMemo(() => themeClassName(colorTheme), [colorTheme]);

    return (
      // Width is intentionally NOT set here: as a block element it fills the
      // parent until the snap effect assigns the device-pixel-exact width/
      // height (≤8 device px smaller); margin keeps the shrunk box centered.
      // React never writes `width`/`height` for this element, so the effect's
      // imperative styles survive re-renders.
      <div
        ref={outerRef}
        style={{position: 'relative', margin: '0 auto'}}
        onMouseDownCapture={onContainerMouseDown}>
        <div
          ref={containerRef}
          className={`cg-wrap ${themeClass}`}
          // Deliberately NO height:100% — aspectRatio alone derives the height
          // from the width, which equals the snap effect's square size once it
          // has run, and stays immune to align-items:stretch giving the outer
          // div a definite height in row-flex/grid ancestors pre-snap.
          style={{width: '100%', aspectRatio: '1 / 1'}}
        />
        {pendingPromotion && (
          <Promotion
            color={pendingPromotion.color}
            toSquare={pendingPromotion.to}
            orientation={boardOrientation}
            onSelect={onPromotionSelect}
            onCancel={onPromotionCancel}
          />
        )}
      </div>
    );
  },
);

export default Chessboard;
